import React from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

/**
 * Buttons, per handoff-academic/CLAUDE.md §4.
 *
 * Three things changed with the Academic direction:
 *
 *   1. No shadows and no glow on solid fills. Letterpress gets its hierarchy
 *      from hairline rules, weight and spacing; a drop-shadowed button reads as
 *      a different product. `shadow-*` is for overlays only now.
 *   2. Hover moves to the `-hover` token instead of `opacity-85`. Fading a
 *      grenat fill toward parchment desaturates it; the token is a real,
 *      designed second step (and `-active` a third).
 *   2b. The solid fill reads `--color-primary-SOLID`, not `--color-primary`.
 *      Those are the same grenat in light mode but diverge on Night, where
 *      --color-primary is the lifted claret so that links and icons stay
 *      legible ON the dark page. A claret BUTTON is a different question from
 *      claret TEXT: creme on claret is only 2.81:1 and fails. So the button
 *      keeps the brand grenat in both themes and earns its edge from a gilt
 *      hairline (7.70:1 on Night) rather than from the fill. Measured, not
 *      assumed — grenat on Night is 1.05:1, which is no edge at all.
 *   3. The focus ring is the two-stop `.focus-ring` box-shadow, not
 *      `ring-2 ring-offset-1` — an outline-style ring gets clipped inside the
 *      `overflow-hidden` table cells where most of these buttons live.
 *
 * The pairing rule from §4, which has teeth: a grenat primary is dark and a
 * vermilion destructive only a little lighter, so **never render a solid
 * primary beside a solid destructive**. In destructive confirms the destructive
 * keeps the solid fill and the safe action steps down to `ghost` or `outline`.
 */
const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-colors duration-200 focus-ring disabled:pointer-events-none disabled:opacity-45",
    {
        variants: {
            variant: {
                default: "bg-primary-solid text-primary-solid-foreground border border-primary-solid-border hover:bg-primary-solid-hover active:bg-primary-active",
                // Alias. cva emits no classes for an unmatched variant value, so
                // `variant="primary"` used to render a button with no background
                // and no text colour — invisible, not merely off-style.
                primary: "bg-primary-solid text-primary-solid-foreground border border-primary-solid-border hover:bg-primary-solid-hover active:bg-primary-active",
                destructive: "bg-destructive text-destructive-foreground hover:bg-error",
                outline: "border border-primary bg-transparent text-primary hover:bg-primary-subtle",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary",
                ghost: "hover:bg-muted hover:text-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                accent: "bg-accent text-accent-foreground hover:bg-accent",
                success: "bg-success text-success-foreground hover:bg-success",
                warning: "bg-warning text-warning-foreground hover:bg-warning",
                danger: "bg-error text-error-foreground hover:bg-error",
            },
            size: {
                // §4 heights: sm 32, md 36, lg 44.
                default: "h-9 px-4 text-sm",
                sm: "h-8 px-3 text-xs",
                lg: "h-11 px-5 text-sm",
                icon: "h-9 w-9",
                xs: "h-7 px-2.5 text-xs",
                xl: "h-11 px-8 text-base",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";

    const iconSizeMap = {
        xs: 12, sm: 13, default: 14, lg: 16, xl: 18, icon: 14,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 14;

    const LoadingSpinner = () => (
        <svg className="animate-spin ltr:-ml-1 ltr:mr-1.5 rtl:-mr-1 rtl:ml-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const renderIcon = () => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
 children && iconPosition === 'left' && "ltr:mr-1.5 rtl:ml-1.5",
                        children && iconPosition === 'right' && "ltr:ml-1.5 rtl:mr-1.5"
                    )}
                />
            );
        } catch {
            return null;
        }
    };

    const renderFallbackButton = () => (
        <button
            className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </button>
    );

    if (asChild) {
        try {
            if (!children || React.Children?.count(children) !== 1) return renderFallbackButton();
            const child = React.Children?.only(children);
            if (!React.isValidElement(child)) return renderFallbackButton();

            const content = (
                <>
                    {loading && <LoadingSpinner />}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {child?.props?.children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            );

            const clonedChild = React.cloneElement(child, {
                className: cn(buttonVariants({ variant, size, className }), fullWidth && "w-full", child?.props?.className),
                disabled: disabled || loading || child?.props?.disabled,
                children: content,
            });

            return <Comp ref={ref} {...props}>{clonedChild}</Comp>;
        } catch {
            return renderFallbackButton();
        }
    }

    return (
        <Comp
            className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </Comp>
    );
});

Button.displayName = "Button";
export default Button;
